import React, { useState, useMemo, useCallback } from "react";
import { useSelector } from "react-redux";
import Table from "../../../shared/components/common/Table";
import SearchBar from "../../../shared/components/common/SearchBar";
import TableToolbar from "../../../shared/components/common/TableToolbar";
import UserManagementHeader from "./UserManagementHeader";
import EditUserModal from "./EditUserModal";
import AddUserModal from "./AddUserModal";
import DeleteUserModal from "./DeleteUserModal";
import BulkImportModal from "./BulkImportModal";
import SelectionToolbar from "../../../shared/components/common/SelectionToolbar";
import { useForm } from "../../../shared/hooks/useForm";
import useUsers from "../hooks/useUsers";
import useUserFilters from "../hooks/useUserFilters";
import useVoicemailNumbers from "../hooks/useVoicemailNumbers";
import { buildUserColumns } from "./userTableColumns";
import { exportUsersToCsv, printUsersTable } from "../utils/userTableExport";
import { selectCurrentUser } from "../../auth/store/authSlice";
import { updatePhoneNumberAssociation } from "../../numbers/services/numbersService";
import "../../logs/Logs.css";

const EMAIL_MODE_OPTIONS = [
  { name: "All Email Modes", value: "all" },
  { name: "Enabled", value: "Enabled" },
  { name: "Disabled", value: "Disabled" },
];

const SMS_MODE_OPTIONS = [
  { name: "All SMS Modes", value: "all" },
  { name: "Enabled", value: "Enabled" },
  { name: "Disabled", value: "Disabled" },
];

const ADD_FORM_INITIAL_STATE = {
  firstName: { value: "", isValid: false },
  lastName: { value: "", isValid: false },
  deliveryAddress: { value: "", isValid: false },
  password: { value: "", isValid: false },
  emailMode: { value: "Enabled", isValid: true },
  smsMode: { value: "Disabled", isValid: true },
  mobileNumber: { value: "", isValid: true },
  voicemailNumberId: { value: "", isValid: true },
  timeZone: { value: "UTC", isValid: true },
  userRole: { value: "viewer", isValid: true },
};

const UserManagement = () => {
  const {
    users,
    isLoading,
    isProcessing,
    fetchUsers,
    handleAddUser: apiAddUser,
    handleUpdateUser: apiUpdateUser,
    handleDeleteUser: apiDeleteUser,
    handleBulkDelete: apiBulkDelete,
    handleBulkStatusChange: apiBulkStatusChange,
  } = useUsers();

  const currentUser = useSelector(selectCurrentUser);

  const [currentPage, setCurrentPage] = useState(0);
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [spreadTable, setSpreadTable] = useState(false);

  const [showEditModal, setShowEditModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [showBulkImportModal, setShowBulkImportModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [addErrorMessage, setAddErrorMessage] = useState("");
  const [editErrorMessage, setEditErrorMessage] = useState("");
  const [pendingUserId, setPendingUserId] = useState(null);

  const currentUserEmail = useMemo(
    () => String(currentUser?.email || "").trim().toLowerCase(),
    [currentUser?.email],
  );
  const resolvedClientId = useMemo(
    () => Number(currentUser?.client_id || 0) || null,
    [currentUser?.client_id],
  );

  const {
    loadPhoneNumbers,
    usersWithVoicemailNumbers,
    availableVoicemailNumbers,
    editVoicemailNumbers,
    selectedVoicemailLabel,
  } = useVoicemailNumbers({ users, selectedUser, clientId: resolvedClientId });

  const {
    searchTerm,
    setSearchTerm,
    deliveryAddressFilter,
    setDeliveryAddressFilter,
    emailModeFilter,
    setEmailModeFilter,
    smsModeFilter,
    setSmsModeFilter,
    filteredUsers,
  } = useUserFilters(usersWithVoicemailNumbers);

  const [editFormState, editInputHandler, editSelectHandler, setEditFormData] =
    useForm(
      {
        firstName: { value: "", isValid: false },
        lastName: { value: "", isValid: false },
        userStatus: { value: "Enabled", isValid: true },
        smsMode: { value: "Disabled", isValid: true },
        mobileNumber: { value: "", isValid: true },
        voicemailNumberId: { value: "", isValid: true },
        timeZone: { value: "UTC", isValid: true },
      },
      false,
    );

  const [addFormState, addInputHandler, addSelectHandler, setAddFormData] =
    useForm(ADD_FORM_INITIAL_STATE, false);

  const handleEdit = useCallback(
    (user) => {
      setSelectedUser(user);
      setEditErrorMessage("");
      setEditFormData(
        {
          firstName: { value: user.firstName, isValid: true },
          lastName: { value: user.lastName, isValid: true },
          userStatus: { value: user.emailMode, isValid: true },
          smsMode: { value: user.smsMode, isValid: true },
          mobileNumber: { value: user.mobileNumber, isValid: true },
          voicemailNumberId: {
            value: user.voicemailNumberId ? String(user.voicemailNumberId) : "",
            isValid: true,
          },
          timeZone: { value: user.timeZone, isValid: true },
        },
        true,
      );
      setShowEditModal(true);
    },
    [setEditFormData],
  );

  const handleEditSubmit = useCallback(async () => {
    if (!selectedUser) return;
    const formData = {
      firstName: editFormState.inputs.firstName.value,
      lastName: editFormState.inputs.lastName.value,
      userStatus: editFormState.inputs.userStatus.value,
      smsMode: editFormState.inputs.smsMode.value,
      mobileNumber: editFormState.inputs.mobileNumber.value,
      voicemailNumberId: editFormState.inputs.voicemailNumberId.value,
      timeZone: editFormState.inputs.timeZone.value,
    };
    const result = await apiUpdateUser(selectedUser.id, formData, selectedUser);
    if (result?.error) {
      setEditErrorMessage(result.error);
      return;
    }

    const nextVoicemailId = formData.voicemailNumberId || "";
    const currentVoicemailId = selectedUser.voicemailNumberId
      ? String(selectedUser.voicemailNumberId)
      : "";

    if (nextVoicemailId !== currentVoicemailId) {
      if (currentVoicemailId) {
        const response = await updatePhoneNumberAssociation(currentVoicemailId, {
          is_associated: false,
          user_id: selectedUser.id,
        });
        if (response?.error) {
          setEditErrorMessage(
            response.error.message || "Unable to unassign voicemail number.",
          );
          return;
        }
      }

      if (nextVoicemailId) {
        const response = await updatePhoneNumberAssociation(nextVoicemailId, {
          is_associated: true,
          user_id: selectedUser.id,
        });
        if (response?.error) {
          setEditErrorMessage(
            response.error.message || "Unable to assign voicemail number.",
          );
          return;
        }
      }
    }

    await loadPhoneNumbers();
    await fetchUsers();

    setShowEditModal(false);
    setSelectedUser(null);
    setEditErrorMessage("");
  }, [
    selectedUser,
    editFormState,
    apiUpdateUser,
    fetchUsers,
    loadPhoneNumbers,
  ]);

  const handleDelete = useCallback(
    (userId) => {
      const user = users.find((u) => u.id === userId);
      setSelectedUser(user);
      setShowDeleteModal(true);
    },
    [users],
  );

  const handleDeleteConfirm = useCallback(async () => {
    if (!selectedUser) return;
    await apiDeleteUser(selectedUser.id);
    setShowDeleteModal(false);
    setSelectedUser(null);
  }, [selectedUser, apiDeleteUser]);

  const handleAddUser = useCallback(() => {
    setAddFormData(ADD_FORM_INITIAL_STATE, false);
    setAddErrorMessage("");
    setPendingUserId(null);
    setShowAddModal(true);
  }, [setAddFormData]);

  const handleAddSubmit = useCallback(async () => {
    const formData = {
      firstName: addFormState.inputs.firstName.value,
      lastName: addFormState.inputs.lastName.value,
      deliveryAddress: addFormState.inputs.deliveryAddress.value,
      password: addFormState.inputs.password.value,
      emailMode: addFormState.inputs.emailMode.value,
      smsMode: addFormState.inputs.smsMode.value,
      mobileNumber: addFormState.inputs.mobileNumber.value,
      voicemailNumberId: addFormState.inputs.voicemailNumberId.value,
      timeZone: addFormState.inputs.timeZone.value,
      userRole: addFormState.inputs.userRole.value,
    };

    if (pendingUserId) {
      if (!formData.voicemailNumberId) {
        setAddErrorMessage("Select a voicemail number to finish association.");
        return;
      }

      const response = await updatePhoneNumberAssociation(
        formData.voicemailNumberId,
        {
          is_associated: true,
          user_id: pendingUserId,
        },
      );

      if (response?.error) {
        setAddErrorMessage(
          response.error.message || "Unable to assign voicemail number.",
        );
        return;
      }

      await loadPhoneNumbers();
      await fetchUsers();
      setPendingUserId(null);
      setShowAddModal(false);
      setAddErrorMessage("");
      return;
    }

    const result = await apiAddUser(formData);
    if (result?.error) {
      setAddErrorMessage(result.error);
      return;
    }

    if (formData.voicemailNumberId && result?.userId) {
      const response = await updatePhoneNumberAssociation(
        formData.voicemailNumberId,
        {
          is_associated: true,
          user_id: result.userId,
        },
      );

      if (response?.error) {
        setPendingUserId(result.userId);
        setAddErrorMessage(
          response.error.message ||
            "User created, but voicemail association failed. Please retry.",
        );
        return;
      }
    }

    await loadPhoneNumbers();
    await fetchUsers();
    setShowAddModal(false);
    setAddErrorMessage("");
  }, [
    addFormState,
    apiAddUser,
    fetchUsers,
    loadPhoneNumbers,
    pendingUserId,
  ]);

  const handleBulkImport = useCallback(() => {
    setShowBulkImportModal(true);
  }, []);

  const handleBulkImportSubmit = useCallback((file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const text = e.target.result;
        const lines = text.split("\n").filter((line) => line.trim());
        if (lines.length < 2) {
          alert("CSV file is empty or invalid");
          return;
        }
        alert(`CSV parsed: ${lines.length - 1} row(s) found. Bulk API import not yet supported.`);
      } catch (error) {
        console.error("Error parsing CSV:", error);
        alert("Error parsing CSV file. Please check the format.");
      } finally {
        setShowBulkImportModal(false);
      }
    };
    reader.onerror = () => {
      alert("Error reading file");
      setShowBulkImportModal(false);
    };
    reader.readAsText(file);
  }, []);

  const handleCancel = useCallback(() => {
    setShowEditModal(false);
    setShowAddModal(false);
    setShowDeleteModal(false);
    setShowBulkDeleteModal(false);
    setShowBulkImportModal(false);
    setSelectedUser(null);
    setAddErrorMessage("");
    setEditErrorMessage("");
    setPendingUserId(null);
  }, []);

  const handleBulkEnable = useCallback(async () => {
    const ids = Array.from(selectedRows);
    if (ids.length === 0) return;
    await apiBulkStatusChange(ids, "enabled");
    setSelectedRows(new Set());
  }, [selectedRows, apiBulkStatusChange]);

  const handleBulkDisable = useCallback(async () => {
    const ids = Array.from(selectedRows);
    if (ids.length === 0) return;
    await apiBulkStatusChange(ids, "disabled");
    setSelectedRows(new Set());
  }, [selectedRows, apiBulkStatusChange]);

  const handleBulkDelete = useCallback(() => {
    if (selectedRows.size === 0) return;
    setShowBulkDeleteModal(true);
  }, [selectedRows]);

  const handleBulkDeleteConfirm = useCallback(async () => {
    const ids = Array.from(selectedRows);
    await apiBulkDelete(ids);
    setSelectedRows(new Set());
    setShowBulkDeleteModal(false);
  }, [selectedRows, apiBulkDelete]);

  const handleFilter = () => {};

  const handleDownload = useCallback(() => {
    exportUsersToCsv(filteredUsers);
  }, [filteredUsers]);

  const handleSpreadTableToggle = useCallback(() => {
    setSpreadTable((prev) => !prev);
  }, []);

  const columns = useMemo(
    () =>
      buildUserColumns({
        selectedRows,
        setSelectedRows,
        filteredUsers,
        onEdit: handleEdit,
        onDelete: handleDelete,
        currentUserEmail,
      }),
    [selectedRows, filteredUsers, handleEdit, handleDelete, currentUserEmail],
  );

  return (
    <div className="flex flex-col w-full h-full bg-transparent">
      <UserManagementHeader
        onBulkImport={handleBulkImport}
        onAddUser={handleAddUser}
        useMUIcons={true}
      />

      <div
        className={`sm:mx-2 border border-line rounded-t-xl shadow-sm flex flex-col flex-1 logs-table-area ${
          spreadTable ? " table-zoom-out" : ""
        }`}
      >
        <div className="p-4 bg-white border-b border-line flex max-[400px]:flex-col flex-row justify-between sm:items-center rounded-t-xl max-[400px]:gap-4 gap-2 sm:gap-0">
          <SearchBar
            value={searchTerm}
            onChange={setSearchTerm}
            placeholder="Search users"
            useMUIicons={false}
          />

          <TableToolbar
            useMUIicons={false}
            spreadTable={spreadTable}
            onSpreadTableToggle={handleSpreadTableToggle}
            onFilter={handleFilter}
            onPrint={printUsersTable}
            onDownload={handleDownload}
            displayData={filteredUsers}
            columns={columns}
            deliveryAddressFilter={deliveryAddressFilter}
            setDeliveryAddressFilter={setDeliveryAddressFilter}
            emailModeFilter={emailModeFilter}
            setEmailModeFilter={setEmailModeFilter}
            smsModeFilter={smsModeFilter}
            setSmsModeFilter={setSmsModeFilter}
            emailModeOptions={EMAIL_MODE_OPTIONS}
            smsModeOptions={SMS_MODE_OPTIONS}
            exportFilename="user-management"
            exportTitle="PBXScribe User Management"
          />
        </div>

        <SelectionToolbar
          selectedCount={selectedRows.size}
          onEnable={handleBulkEnable}
          onDisable={handleBulkDisable}
          onDelete={handleBulkDelete}
        />

        <div className="flex-1 overflow-auto">
          <Table
            columns={columns}
            data={filteredUsers}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            selectedRows={selectedRows}
            pageSize={10}
            showTotalRecords={true}
            totalRecords={filteredUsers.length}
            showPageSizeSelector={true}
            useMUIcons={false}
            rowIdKey="id"
            loading={isLoading || isProcessing}
          />
        </div>
      </div>

      <EditUserModal
        show={showEditModal}
        formState={editFormState}
        inputHandler={editInputHandler}
        selectHandler={editSelectHandler}
        onCancel={handleCancel}
        onSubmit={handleEditSubmit}
        isProcessing={isProcessing}
        errorMessage={editErrorMessage}
        voicemailNumbers={editVoicemailNumbers}
        selectedVoicemailLabel={selectedVoicemailLabel}
      />

      <AddUserModal
        show={showAddModal}
        formState={addFormState}
        inputHandler={addInputHandler}
        selectHandler={addSelectHandler}
        onCancel={handleCancel}
        onSubmit={handleAddSubmit}
        isProcessing={isProcessing}
        errorMessage={addErrorMessage}
        voicemailNumbers={availableVoicemailNumbers}
      />

      <DeleteUserModal
        show={showDeleteModal}
        onCancel={handleCancel}
        onConfirm={handleDeleteConfirm}
      />

      <DeleteUserModal
        show={showBulkDeleteModal}
        voicemailNumbers={availableVoicemailNumbers}
        onCancel={handleCancel}
        onConfirm={handleBulkDeleteConfirm}
        count={selectedRows.size}
      />

      <BulkImportModal
        show={showBulkImportModal}
        onCancel={handleCancel}
        onImport={handleBulkImportSubmit}
      />
    </div>
  );
};

export default UserManagement;
